import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useCompanyPlan } from '../hooks/useCompanyPlan';
import Layout from '../components/Layout';
import {
  TrendingUp, Users, Eye, Briefcase,
  Lock, BarChart2, Download
} from 'lucide-react';

export default function Analytics() {
  const { company, refreshCompany } = useAuth();
  const { can } = useCompanyPlan(company, refreshCompany);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);

  useEffect(() => {
    if (can.viewAnalytics) {
      loadAnalytics();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can.viewAnalytics, range]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const since = new Date(
        Date.now() - range * 24 * 60 * 60 * 1000
      ).toISOString();

      const [jobsRes, appsRes, viewsRes] = await Promise.all([
        supabase
          .from('job_listings')
          .select('*')
          .eq('company_id', company.id),
        supabase
          .from('job_applications')
          .select('*')
          .eq('company_id', company.id)
          .gte('applied_at', since),
        supabase
          .from('job_views')
          .select('viewed_at')
          .eq('company_id', company.id)
          .gte('viewed_at', since),
      ]);

      const jobs = jobsRes.data || [];
      const apps = appsRes.data || [];
      const views = viewsRes.data || [];

      const statusBreakdown = {};
      apps.forEach((a) => {
        statusBreakdown[a.status] =
          (statusBreakdown[a.status] || 0) + 1;
      });

      const topJobs = jobs
        .map((j) => ({
          ...j,
          appCount: apps.filter((a) => a.job_listing_id === j.id)
            .length,
        }))
        .sort((a, b) => b.appCount - a.appCount)
        .slice(0, 5);

      setStats({
        totalJobs: jobs.length,
        activeJobs: jobs.filter((j) => j.is_active).length,
        totalApps: apps.length,
        totalViews: views.length,
        conversionRate:
          views.length > 0
            ? ((apps.length / views.length) * 100).toFixed(1)
            : '0',
        statusBreakdown,
        topJobs,
        hired: apps.filter((a) => a.status === 'hired').length,
        shortlisted: apps.filter((a) =>
          ['shortlisted', 'interview_scheduled'].includes(a.status)
        ).length,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!stats) return;
    const rows = [
      ['Metric', 'Value'],
      ['Total Jobs', stats.totalJobs],
      ['Active Jobs', stats.activeJobs],
      ['Total Applications', stats.totalApps],
      ['Total Views', stats.totalViews],
      ['Conversion Rate', `${stats.conversionRate}%`],
      ['Hired', stats.hired],
      ['Shortlisted', stats.shortlisted],
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_${range}days.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const STATUS_COLORS = {
    pending: '#F59E0B',
    reviewed: '#4F8EF7',
    shortlisted: '#10B981',
    interview_scheduled: '#7C3AED',
    hired: '#22D3EE',
    rejected: '#EF4444',
  };

  if (!can.viewAnalytics) {
    return (
      <Layout>
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          minHeight: 400, textAlign: 'center', padding: 40,
        }}>
          <div style={{
            width: 80, height: 80, borderRadius: 20,
            background: 'linear-gradient(135deg,#7C3AED,#6D28D9)',
            display: 'flex', alignItems: 'center',
            justifyContent: 'center', marginBottom: 20,
            boxShadow: '0 8px 32px rgba(124,58,237,0.3)',
          }}>
            <BarChart2 size={36} color="#fff" />
          </div>
          <h2 style={{
            fontSize: 22, fontWeight: 800,
            color: '#1E293B', marginBottom: 8,
          }}>
            Analytics Dashboard
          </h2>
          <p style={{
            color: '#64748B', fontSize: 14,
            marginBottom: 16, maxWidth: 380, lineHeight: 1.6,
          }}>
            Get deep insights into your job performance,
            applicant quality, and hiring funnel.
          </p>
          <div style={{
            display: 'flex', gap: 8, flexWrap: 'wrap',
            justifyContent: 'center', marginBottom: 24,
          }}>
            {[
              'View counts per job',
              'Application funnel',
              'Conversion rates',
              'Top performing listings',
              'Export to CSV',
            ].map((f, i) => (
              <span key={i} style={{
                fontSize: 12, padding: '4px 10px',
                background: 'rgba(124,58,237,0.1)',
                color: '#7C3AED', borderRadius: 6,
                fontWeight: 600,
              }}>✓ {f}</span>
            ))}
          </div>
          <div style={{
            background: 'rgba(124,58,237,0.08)',
            border: '1px solid rgba(124,58,237,0.2)',
            borderRadius: 12, padding: '12px 20px',
            marginBottom: 20, fontSize: 13,
            color: '#7C3AED', fontWeight: 600,
          }}>
            🚀 Available on Pro ($165/mo) and Enterprise ($410/mo)
          </div>
          <Link to="/billing" style={{
            padding: '13px 28px', borderRadius: 12,
            background: 'linear-gradient(135deg,#7C3AED,#6D28D9)',
            color: '#fff', fontWeight: 700, fontSize: 14,
            textDecoration: 'none',
            boxShadow: '0 4px 16px rgba(124,58,237,0.3)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Lock size={15} /> Upgrade to Pro
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 24,
        flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h1 style={{
            fontSize: 24, fontWeight: 800, color: '#1E293B',
          }}>Analytics</h1>
          <p style={{ color: '#94A3B8', fontSize: 14, marginTop: 4 }}>
            {company?.name} · Performance insights
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setRange(d)} style={{
              padding: '8px 14px', borderRadius: 8,
              border: range === d
                ? '1.5px solid #7C3AED'
                : '1.5px solid #E2E8F0',
              background: range === d
                ? 'rgba(124,58,237,0.08)' : '#fff',
              color: range === d ? '#7C3AED' : '#64748B',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>
              {d}d
            </button>
          ))}
          {can.exportApplicants && (
            <button onClick={exportCSV} style={{
              padding: '8px 14px', borderRadius: 8,
              border: '1.5px solid #E2E8F0',
              background: '#fff', color: '#64748B',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Download size={13} />Export
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{
          display: 'flex', justifyContent: 'center',
          alignItems: 'center', minHeight: 300,
        }}>
          <div className="spinner" />
        </div>
      ) : stats && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))',
            gap: 14, marginBottom: 24,
          }}>
            {[
              { icon: <Briefcase size={20} />, label: 'Active Jobs', value: stats.activeJobs, color: '#4F8EF7', bg: 'rgba(79,142,247,0.08)' },
              { icon: <Users size={20} />, label: `Applications (${range}d)`, value: stats.totalApps, color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
              { icon: <Eye size={20} />, label: `Views (${range}d)`, value: stats.totalViews, color: '#10B981', bg: 'rgba(16,185,129,0.08)' },
              { icon: <TrendingUp size={20} />, label: 'Conversion', value: `${stats.conversionRate}%`, color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
              { icon: <Users size={20} />, label: 'Hired', value: stats.hired, color: '#22D3EE', bg: 'rgba(34,211,238,0.08)' },
              { icon: <Users size={20} />, label: 'Shortlisted', value: stats.shortlisted, color: '#EC4899', bg: 'rgba(236,72,153,0.08)' },
            ].map((s, i) => (
              <div key={i} style={{
                background: '#fff', borderRadius: 14,
                padding: '18px 16px', border: '1px solid #E2E8F0',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 11,
                  background: s.bg, color: s.color,
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0,
                }}>{s.icon}</div>
                <div>
                  <div style={{
                    fontSize: 22, fontWeight: 800,
                    color: '#1E293B', lineHeight: 1,
                  }}>{s.value}</div>
                  <div style={{
                    fontSize: 11, color: '#94A3B8', marginTop: 3,
                  }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16,
          }}>
            <div style={{
              background: '#fff', borderRadius: 16,
              padding: 20, border: '1px solid #E2E8F0',
            }}>
              <div style={{
                fontWeight: 700, color: '#1E293B',
                fontSize: 14, marginBottom: 16,
              }}>Application Status Breakdown</div>
              {Object.entries(stats.statusBreakdown).length === 0 ? (
                <div style={{
                  textAlign: 'center', color: '#94A3B8',
                  padding: '20px 0', fontSize: 13,
                }}>No applications yet</div>
              ) : Object.entries(stats.statusBreakdown).map(([status, count]) => {
                const total = stats.totalApps || 1;
                const pct = Math.round((count / total) * 100);
                const col = STATUS_COLORS[status] || '#94A3B8';
                return (
                  <div key={status} style={{ marginBottom: 10 }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      marginBottom: 4,
                    }}>
                      <span style={{
                        fontSize: 12, color: '#64748B',
                        textTransform: 'capitalize', fontWeight: 600,
                      }}>
                        {status.replace('_', ' ')}
                      </span>
                      <span style={{
                        fontSize: 12, fontWeight: 700, color: col,
                      }}>
                        {count} ({pct}%)
                      </span>
                    </div>
                    <div style={{
                      height: 6, background: '#F1F5F9',
                      borderRadius: 3, overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%', width: `${pct}%`,
                        background: col, borderRadius: 3,
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{
              background: '#fff', borderRadius: 16,
              padding: 20, border: '1px solid #E2E8F0',
            }}>
              <div style={{
                fontWeight: 700, color: '#1E293B',
                fontSize: 14, marginBottom: 16,
              }}>Top Performing Jobs</div>
              {stats.topJobs.length === 0 ? (
                <div style={{
                  textAlign: 'center', color: '#94A3B8',
                  padding: '20px 0', fontSize: 13,
                }}>No jobs posted yet</div>
              ) : stats.topJobs.map((j, i) => (
                <div key={j.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '8px 0',
                  borderBottom: i < stats.topJobs.length - 1
                    ? '1px solid #F1F5F9' : 'none',
                }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%',
                    background: 'rgba(79,142,247,0.1)',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11, fontWeight: 800,
                    color: '#4F8EF7', flexShrink: 0,
                  }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      color: '#1E293B', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{j.title}</div>
                    <div style={{ fontSize: 11, color: '#94A3B8' }}>
                      {j.views_count || 0} views
                    </div>
                  </div>
                  <span style={{
                    fontSize: 13, fontWeight: 800, color: '#7C3AED',
                  }}>
                    {j.appCount} apps
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}